import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { TableType } from '@common/ts';
import DeleteWorkspace from '@/components/Modals/DeleteWorkspaceModal';
import RenameWorkspaceModal from '@/components/Modals/RenameWorkspaceModal';
import WorkspaceInviteModal from '@/components/Modals/WorkspaceInviteModal';
import MyWorkspaceTable from '@/components/all-tellets-table/MyWorkspaceTable';
import FullPageLoader from '@/components/atoms/Loader/FullPageLoader';
import EllipsisHorizontalIcon from '@/components/icons/EllipsisHorizontalIcon';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import useGetAllWorkspaces from '@/hooks/useGetAllWorkspaces';
import useGetCurrentWorkspace from '@/hooks/useGetCurrentWorkspace';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import FreemiumTooltip from '@/components/tooltip/FreemiumTooltip';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useGetAllProjects } from '@/reactQuery/get';
import { useUrlTable } from '@/hooks/useUrlTable';
import { sortingStateToSortConfig } from '@/utils/sortingStateToSortConfig';

const PAGE_SIZE = 20;

function MyWorkspace() {
  const {
    firstWorkspaceId,
    isLoading: isLoadingWorkspaces,
    mergedWorkspaces,
    ownedWorkspacesCount,
  } = useGetAllWorkspaces();
  const isLastOwnedWorkspace = ownedWorkspacesCount === 1;
  const { isUserOwner, isUserEditor } = useWorkspaceRoles();
  const {
    actions: { clearCurrentProject },
  } = useCurrentProject();
  const {
    urlCurrentPage,
    updateUrlPage,
    urlSorting,
    updateUrlSorting,
    updateUrlFilter,
    getUrlFilterValue,
  } = useUrlTable({ type: TableType.PROJECTS });
  const currentWorkspace = useGetCurrentWorkspace();
  const { name: workspaceName = '' } = currentWorkspace || {};
  const [isDeletingWorkspace, setIsDeletingWorkspace] = useState(false);
  const { workspace_id: workspaceId, organization_id: organizationId } = useParams();

  const titleFilter = getUrlFilterValue('title');
  const filters = useMemo(() => (titleFilter ? { title: titleFilter } : undefined), [titleFilter]);
  const sortConfig = useMemo(() => sortingStateToSortConfig(urlSorting), [urlSorting]);

  const {
    data: { items: projects, pagination } = {},
    isLoading: isLoadingProjects,
    error,
  } = useGetAllProjects(
    organizationId,
    isDeletingWorkspace ? undefined : workspaceId,
    {
      page: urlCurrentPage,
      limit: PAGE_SIZE,
    },
    filters,
    sortConfig,
  );

  // Clear current project when entering workspace dashboard
  useEffect(() => {
    clearCurrentProject();
  }, [clearCurrentProject]);

  if (isLoadingWorkspaces) return <FullPageLoader />;

  const canUserEdit = isUserOwner || isUserEditor;

  return (
    <main className="flex flex-col w-full h-full">
      <FreemiumTooltip />

      <div className="flex items-center gap-7">
        <h1 className="text-xl font-semibold epilogue-font">{workspaceName}</h1>
        <div className="flex items-center gap-1">
          <WorkspaceInviteModal firstWorkspaceId={firstWorkspaceId} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={!canUserEdit}>
              <Button type="button" className="-mb-1" variant="ghost">
                <EllipsisHorizontalIcon className="size-6" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-52 z-100">
              {(isUserOwner || isUserEditor) && (
                <RenameWorkspaceModal currentWorkspaceName={workspaceName} />
              )}
              {isUserOwner && (
                <DeleteWorkspace
                  currentWorkspaceName={workspaceName}
                  mergedWorkspaces={mergedWorkspaces}
                  setIsDeletingWorkspace={setIsDeletingWorkspace}
                  projects={projects || []}
                  isLastOwnedWorkspace={isLastOwnedWorkspace}
                />
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <MyWorkspaceTable
        projects={projects}
        isLoadingProjects={isLoadingProjects}
        error={error}
        mergedWorkspaces={mergedWorkspaces}
        urlCurrentPage={urlCurrentPage}
        updateUrlPage={updateUrlPage}
        urlSorting={urlSorting}
        updateUrlSorting={updateUrlSorting}
        updateUrlFilter={updateUrlFilter}
        getUrlFilterValue={getUrlFilterValue}
        pageSize={PAGE_SIZE}
        totalCount={pagination?.totalItems || 0}
      />
    </main>
  );
}

export default MyWorkspace;
